import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DATA_ELEMENT_TYPES } from "@/utils/dataTypes";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Loader2 } from "lucide-react";
import { DataElement, ElementVersion, SourceFieldMapping } from "@/entities/all";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Maps Avro/Iceberg source metadata types to Apache Arrow types
const AVRO_TO_ARROW_TYPE_MAP = {
  'string': 'string', 'utf8': 'string', 'fixed': 'binary', 'uuid': 'uuid', 'binary': 'binary', 'bytes': 'binary',
  'int': 'int32', 'integer': 'int32', 'short': 'int16',
  'long': 'int64',
  'float': 'float32', 'double': 'float64', 'decimal': 'decimal128',
  'boolean': 'bool',
  'date': 'date32',
  'time': 'time32',
  'timestamp': 'timestamp', 'timestamptz': 'timestamp', 'timestamp_ntz': 'timestamp',
  'struct': 'struct', 'record': 'struct', 'map': 'map',
  'list': 'list', 'array': 'list',
  // Arrow types pass through
  'null': 'null', 'bool': 'bool',
  'int8': 'int8', 'int16': 'int16', 'int32': 'int32', 'int64': 'int64',
  'uint8': 'uint8', 'uint16': 'uint16', 'uint32': 'uint32', 'uint64': 'uint64',
  'float16': 'float16', 'float32': 'float32', 'float64': 'float64',
  'date32': 'date32', 'time32': 'time32', 'time64': 'time64',
  'decimal128': 'decimal128', 'duration': 'duration',
  'large_utf8': 'large_string', 'large_binary': 'large_binary',
};

function mapDataType(t) {
  return t ? AVRO_TO_ARROW_TYPE_MAP[t.toLowerCase().trim()] || 'string' : 'string';
}

export default function CreateElementFromFieldModal({ isOpen, onClose, onSuccess, field, dataSourceId }) {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    canonical_name: '',
    definition: '',
    data_type: 'string',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && field) {
      setFormData({
        canonical_name: field.field_name || '',
        definition: '',
        data_type: mapDataType(field.data_type),
      });
      setStep(null);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, field]);

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.canonical_name || !formData.definition) return;

    setIsSubmitting(true);
    setError(null);

    const userName = user?.first_name + ' ' + user?.last_name || user?.email || 'Unknown User';

    try {
      // Step 1: Create DataElement
      setStep('element');
      const newElement = await DataElement.create({
        canonical_name: formData.canonical_name,
        is_public: false,
        current_version_number: 1,
      });

      // Step 2: Create ElementVersion
      setStep('version');
      await ElementVersion.create({
        element_id: newElement.id,
        version_number: 1,
        changed_by: userName,
        definition: formData.definition,
        data_type: formData.data_type,
        status: 'DRAFT',
        confidence_score: 0.95,
        creation_method: 'AUTOMATED',
        creation_details: {
          source: 'unmapped_field_workflow',
          source_field: field.field_name,
          data_source_id: parseInt(dataSourceId),
        },
        completeness_weight: 1,
        timeliness_weight: 1,
      });

      // Step 3: Create SourceFieldMapping
      setStep('mapping');
      await SourceFieldMapping.create({
        source_id: parseInt(dataSourceId),
        canonical_element_id: newElement.id,
        source_field_name: field.field_name,
        mapping_confidence: 0.8,
        last_validated: new Date().toISOString().split('T')[0],
      });

      toast.success(`Created element "${formData.canonical_name}" and mapped to "${field.field_name}"`);
      onSuccess();
    } catch (err) {
      console.error(`Error during step "${step}":`, err);
      const stepLabel = step === 'element' ? 'creating element' : step === 'version' ? 'creating version' : 'creating mapping';
      setError({
        step: stepLabel,
        message: err.message || `An error occurred while ${stepLabel}.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubmitLabel = () => {
    if (!isSubmitting) return 'Create Element & Mapping';
    if (step === 'element') return 'Creating Element...';
    if (step === 'version') return 'Creating Version...';
    if (step === 'mapping') return 'Creating Mapping...';
    return 'Creating...';
  };

  if (!field) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Element from Unmapped Field</DialogTitle>
          <DialogDescription>
            Create a new data element and map it to this source field in one step.
          </DialogDescription>
        </DialogHeader>

        {/* Read-only context panel */}
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-slate-500">Field Name</div>
              <div className="font-medium text-slate-900">{field.field_name}</div>
            </div>
            <div>
              <div className="text-slate-500">Data Type</div>
              <div className="font-medium text-slate-900">
                {field.data_type || 'unknown'}
                <span className="text-slate-400 mx-1">&rarr;</span>
                <Badge variant="outline" className="text-xs">{mapDataType(field.data_type)}</Badge>
              </div>
            </div>
            {field.count !== undefined && field.count !== null && (
              <div>
                <div className="text-slate-500">Count</div>
                <div className="font-medium text-slate-900">{field.count?.toLocaleString()}</div>
              </div>
            )}
            {field.null_percentage !== undefined && field.null_percentage !== null && (
              <div>
                <div className="text-slate-500">Null %</div>
                <div className="font-medium text-slate-900">{field.null_percentage?.toFixed(2)}%</div>
              </div>
            )}
            {field.distinct_count !== undefined && field.distinct_count !== null && (
              <div>
                <div className="text-slate-500">Distinct Values</div>
                <div className="font-medium text-slate-900">{field.distinct_count?.toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <span className="font-medium">Failed while {error.step}:</span> {error.message}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="canonical_name">Canonical Name *</Label>
            <Input
              id="canonical_name"
              value={formData.canonical_name}
              onChange={(e) => handleChange('canonical_name', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="definition">Definition *</Label>
            <Textarea
              id="definition"
              value={formData.definition}
              onChange={(e) => handleChange('definition', e.target.value)}
              placeholder="Describe what this data element represents..."
              rows={3}
              required
            />
          </div>

          <div>
            <Label htmlFor="data_type">Data Type *</Label>
            <Select value={formData.data_type} onValueChange={(value) => handleChange('data_type', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATA_ELEMENT_TYPES.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {getSubmitLabel()}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
